/**
 * createOnboardingGuard — FW-200. Ölçülen ihlal: sihirbaz 6 adımdı VE hiçbir
 * router guard yoktu — kurulumu tamamlamamış bir oturum doğrudan `/`e
 * (modüllere) erişebiliyordu, "kurulum bitmeden modül açılmaz" kuralı yalnız
 * OnboardingView'in kendi kabuğuyla (AppShell'siz render) korunuyordu, YOL
 * SEVİYESİNDE değil. apps/console/src/router/sessionGuard.ts BİREBİR örnek
 * alındı (aynı `RouteLike`/`GuardResult` deseni, aynı gerekçe — bkz. o
 * dosyanın başlığı).
 *
 * Kural (brief'in kendi cümlesi): "oturum var + kurulum TAMAMLANMAMIŞ ->
 * /kurulum dışındaki her rota /kurulum'a; kurulum tamam -> /kurulum -> /".
 *
 * FW-212 GÜNCELLEMESİ: kimliği doğrulanmamış istekler ZORUNLU ve doğrulanmış
 * VITE_AUTH_SURFACE_URL'e TAM SAYFA yönlendirilir. Oturum önyüklemesi
 * backend'in sorumluluğundadır; ağ hatası mevcut /kurulum rota davranışını
 * korur.
 *
 * MUTASYON KANITI: bu guard main.ts'ten kaldırılırsa (veya `hasCompletedOnboarding`
 * her zaman `true` dönerse) onboardingGuard.spec.ts'teki "no session redirects
 * to the login surface" ve "completed session cannot re-enter /kurulum" testleri
 * KIRMIZI döner (bkz. o dosya).
 */
import type { ApiClient } from '@fusapp-work/api'
import { hasCompletedOnboarding } from '../domain/sessionOutcome'
import { useCurrentSession } from '../composables/useCurrentSession'

export interface RouteLike {
  readonly name?: string | symbol | null
}

export type GuardResult = true | false | { readonly name: 'onboarding' } | { readonly name: 'home' }

export interface CreateOnboardingGuardOptions {
  /** Zorunlu ortak Cosmos login origin'i. main.ts VITE_AUTH_SURFACE_URL'i dogrular ve buraya verir. */
  readonly loginUrl: string
}

export function createOnboardingGuard(
  apiClient: ApiClient,
  options: CreateOnboardingGuardOptions,
): (to: RouteLike) => Promise<GuardResult> {
  const loginUrl = options.loginUrl

  return async function onboardingGuard(to: RouteLike): Promise<GuardResult> {
    const state = await useCurrentSession().resolve(apiClient)

    if (state.status === 'unauthenticated') {
      // Bkz. dosya başlığı "FW-212 GÜNCELLEMESİ" — /kurulum'a DÜŞÜLMEZ,
      // gerçek kimlik doğrulama yüzeyine tam sayfa yönlendirilir.
      if (typeof window !== 'undefined') window.location.assign(loginUrl)
      return false
    }

    const completed = state.status === 'authenticated' && state.session !== null && hasCompletedOnboarding(state.session)

    if (to.name === 'onboarding') {
      // Kurulum zaten tamamlanmışsa sihirbaza dönülemez (design 06-ozet
      // spec'i: "Başarıda ... App açılır" — geri dönüş yok).
      return completed ? { name: 'home' } : true
    }

    // /kurulum dışındaki HER rota: kurulum tamam değilse /kurulum'a düşer.
    return completed ? true : { name: 'onboarding' }
  }
}
